import { toast } from "sonner";
import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { IndianRupee, RefreshCw, TrendingUp, Wallet } from "lucide-react";

const KIND_LABEL = {
  campaign_reactivation: "Campaign fee",
  wallet_topup: "Wallet top-up",
};

const KIND_STYLES = {
  campaign_reactivation: "text-amber-500 border-amber-500/40 bg-amber-500/5",
  wallet_topup: "text-emerald-500 border-emerald-500/40 bg-emerald-500/5",
};

const fmtINR = (n) =>
  `₹${Number(n ?? 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const StatCard = ({ icon: Icon, label, value, testid, hint }) => (
  <Card className="p-5" data-testid={testid}>
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
        <p className="font-heading text-3xl font-semibold mt-1 tabular-nums">{value}</p>
        {hint ? (
          <p className="text-xs text-muted-foreground mt-1">{hint}</p>
        ) : null}
      </div>
      <div className="h-10 w-10 rounded-xl bg-primary/20 text-primary grid place-items-center">
        <Icon className="h-5 w-5" />
      </div>
    </div>
  </Card>
);

export default function SuperAdminAnalyticsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [kindFilter, setKindFilter] = useState("all");
  const [error, setError] = useState(null);

  const load = async (opts = {}) => {
    const params = {};
    if (kindFilter && kindFilter !== "all") params.kind = kindFilter;
    params.limit = 200;
    if (opts.refresh) setRefreshing(true); else setLoading(true);
    setError(null);
    try {
      const r = await api.adminPayments(params);
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Unable to load revenue ledger.");
      toast.error(e?.response?.data?.detail || "Couldn't load data");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kindFilter]);

  const summary = data?.summary || {};
  const payments = data?.payments || [];

  const emptyMsg = useMemo(() => {
    if (kindFilter === "campaign_reactivation") return "No campaign fee payments yet.";
    if (kindFilter === "wallet_topup") return "No wallet top-ups yet.";
    return "No payments recorded yet. Revenue will appear here as vendors pay campaign fees or top up their wallets.";
  }, [kindFilter]);

  return (
    <div className="space-y-6" data-testid="sa-revenue-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-semibold">Revenue ledger</h1>
          <p className="text-muted-foreground text-sm">
            Actual sales — from vendor campaign fees and wallet top-ups.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={kindFilter} onValueChange={setKindFilter}>
            <SelectTrigger className="w-[200px]" data-testid="sa-revenue-kind-filter">
              <SelectValue placeholder="Filter by kind" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All revenue</SelectItem>
              <SelectItem value="campaign_reactivation">Campaign fees</SelectItem>
              <SelectItem value="wallet_topup">Wallet top-ups</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="icon"
            onClick={() => load({ refresh: true })}
            disabled={refreshing || loading}
            data-testid="sa-revenue-refresh"
            aria-label="Refresh"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {loading && !data ? (
          [0, 1, 2, 3].map((i) => (
            <Card key={i} className="p-5"><Skeleton className="h-16 w-full" /></Card>
          ))
        ) : (
          <>
            <StatCard
              icon={IndianRupee}
              label="Total revenue"
              value={fmtINR(summary.total_revenue)}
              testid="sa-revenue-total"
            />
            <StatCard
              icon={TrendingUp}
              label="Campaign fees"
              value={fmtINR(summary.campaign_revenue)}
              testid="sa-revenue-campaign"
            />
            <StatCard
              icon={Wallet}
              label="Wallet top-ups"
              value={fmtINR(summary.wallet_revenue)}
              testid="sa-revenue-wallet"
            />
            <StatCard
              icon={IndianRupee}
              label="Payments shown"
              value={Number(summary.count ?? 0).toLocaleString("en-IN")}
              testid="sa-revenue-count"
              hint={`limit 200 · ${kindFilter === "all" ? "all kinds" : KIND_LABEL[kindFilter]}`}
            />
          </>
        )}
      </div>

      {/* Ledger */}
      <Card className="p-0 overflow-hidden" data-testid="sa-revenue-ledger">
        <div className="p-5 border-b border-border">
          <h2 className="font-heading text-lg font-semibold">Payment ledger</h2>
          <p className="text-xs text-muted-foreground">
            Source of truth: <code>GET /api/admin/payments</code>
          </p>
        </div>

        {error ? (
          <div className="p-8 text-center text-sm text-destructive" data-testid="sa-revenue-error">
            {error}
          </div>
        ) : loading && !data ? (
          <div className="p-5 space-y-3">
            {[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
          </div>
        ) : payments.length === 0 ? (
          <div className="p-10 text-center" data-testid="sa-revenue-empty">
            <p className="text-sm text-muted-foreground">{emptyMsg}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Vendor</TableHead>
                  <TableHead>Kind</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Payment ID</TableHead>
                  <TableHead>Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payments.map((p, idx) => (
                  <TableRow key={p.id || p.razorpay_payment_id || idx} data-testid={`sa-revenue-row-${idx}`}>
                    <TableCell>
                      <div className="font-medium">{p.vendor_business || "—"}</div>
                      <div className="text-xs text-muted-foreground">
                        {p.vendor_email || p.vendor_id || ""}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={KIND_STYLES[p.kind] || "text-muted-foreground border-border"}
                      >
                        {KIND_LABEL[p.kind] || p.kind || "unknown"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right tabular-nums font-medium">
                      {fmtINR(p.amount)}
                    </TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {p.razorpay_payment_id || p.id || "—"}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {fmtDate(p.created_at)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}
